import React from 'react';
import { CalendarIcon, FileTextIcon, AlertCircleIcon, ClockIcon } from 'lucide-react';
import { ProgressTracker } from '../components/ProgressTracker';
import { ActionRequiredPanel } from '../components/ActionRequiredPanel';
import { ActivityTimeline } from '../components/ActivityTimeline';
import { MessagingPanel } from '../components/MessagingPanel';
import { PropertySidebar } from '../components/PropertySidebar';
import { Agency } from '../types';
import { mockOrderStages, mockActionItems, mockActivityEvents, mockMessages, mockPropertyData, mockLoanData, mockParties } from '../data/mockData';
interface OrderDashboardPageProps {
  agency: Agency;
}
export function OrderDashboardPage({
  agency
}: OrderDashboardPageProps) {
  const stats = [{
    label: 'Days Open',
    value: '5',
    icon: CalendarIcon,
    bg: 'bg-slate-100',
    iconColor: 'text-slate-600'
  }, {
    label: 'Documents',
    value: '3',
    icon: FileTextIcon,
    bg: 'bg-blue-50',
    iconColor: 'text-blue-600'
  }, {
    label: 'Action Items',
    value: '3',
    icon: AlertCircleIcon,
    bg: 'bg-rose-50',
    iconColor: 'text-rose-600'
  }, {
    label: 'Est. Close',
    value: 'Oct 28, 2024',
    icon: ClockIcon,
    bg: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
    isDate: true
  }];
  return <div className="flex flex-col h-full bg-slate-50 overflow-y-auto" data-id="element-165">
      {/* Dashboard Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 shrink-0" data-id="element-166">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-167">
          <div data-id="element-168">
            <div className="flex items-center gap-3 mb-0.5" data-id="element-169">
              <h1 className="text-xl font-bold text-slate-900" data-id="element-170">
                Order TO-2024-8891
              </h1>
              <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700 ring-1 ring-inset ring-blue-700/10" data-id="element-171">
                Open
              </span>
            </div>
            <p className="text-sm text-slate-500" data-id="element-172">
              Assigned to{' '}
              <span className="font-medium text-slate-700" data-id="element-173">{agency.name}</span>
            </p>
          </div>

          <div className="flex items-center gap-4" data-id="element-174">
            <div className="text-right hidden sm:block" data-id="element-175">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold" data-id="element-176">
                Current Stage
              </p>
              <p className="text-sm font-semibold text-slate-900" data-id="element-177">
                Closing Prep
              </p>
            </div>
            <button className="inline-flex items-center justify-center rounded-lg bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50 transition-colors" data-id="element-178">
              Order Actions
            </button>
          </div>
        </div>
      </div>

      {/* Top Stats Bar */}
      <div className="px-6 py-4 shrink-0" data-id="element-179">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-180">
          {stats.map(stat => <div key={stat.label} className="bg-white rounded-lg border border-slate-200 p-3.5 flex items-center gap-3 shadow-sm" data-id="element-181">
              <div className={`${stat.bg} p-2 rounded-lg shrink-0`} data-id="element-182">
                <stat.icon className={`h-4 w-4 ${stat.iconColor}`} data-id="element-183" />
              </div>
              <div className="min-w-0" data-id="element-184">
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold" data-id="element-185">
                  {stat.label}
                </p>
                <p className={`font-semibold text-slate-900 leading-tight ${stat.isDate ? 'text-sm mt-0.5' : 'text-lg'}`} data-id="element-186">
                  {stat.value}
                </p>
              </div>
            </div>)}
        </div>
      </div>

      {/* Main Layout Grid */}
      <div className="px-6 pb-6 flex-1 min-h-0" data-id="element-187">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-full" data-id="element-188">
          {/* Left Column (Main Content) */}
          <div className="lg:col-span-2 space-y-4 flex flex-col min-h-0" data-id="element-189">
            <ProgressTracker stages={mockOrderStages} data-id="element-190" />

            <ActionRequiredPanel items={mockActionItems} data-id="element-191" />

            {/* Messages Panel */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 flex-1 flex flex-col min-h-[400px]" data-id="element-192">
              <div className="border-b border-slate-200 px-4 flex items-center shrink-0" data-id="element-193">
                <span className="px-3 py-3 text-sm font-medium border-b-2 border-blue-600 text-blue-600" data-id="element-194">
                  Messages
                </span>
                <span className="bg-slate-100 text-slate-600 py-0.5 px-1.5 rounded-full text-[10px] font-semibold ml-2" data-id="element-195">
                  {mockMessages.length}
                </span>
              </div>
              <div className="flex-1 overflow-hidden min-h-0" data-id="element-196">
                <div className="h-full" data-id="element-197">
                  <MessagingPanel messages={mockMessages} embedded data-id="element-198" />
                </div>
              </div>
            </div>
          </div>

          {/* Right Column (Sidebar) */}
          <div className="lg:col-span-1 space-y-4" data-id="element-199">
            <PropertySidebar property={mockPropertyData} loan={mockLoanData} parties={mockParties} data-id="element-200" />

            {/* Activity Card */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-200" data-id="element-201">
              <div className="border-b border-slate-200 px-4 flex items-center shrink-0" data-id="element-202">
                <span className="px-3 py-3 text-sm font-medium text-slate-900" data-id="element-203">
                  Activity
                </span>
              </div>
              <div className="p-4 max-h-[500px] overflow-y-auto" data-id="element-204">
                <ActivityTimeline events={mockActivityEvents} embedded data-id="element-205" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}